// src/components/FormLists/formListsLabGroupOrderUtils.js

import {
  getGroupId,
  normalizeString,
} from '../Spreadsheet/groupedSpreadsheetUtils';

/**
 * formListsLabGroupOrderUtils
 * -----------------------------------------------------------------------------
 * Pure helpers for FormListsLab workspace group ordering.
 *
 * PURPOSE
 *  • Keep spreadsheet group order as a UI preference.
 *  • Avoid mutating field metadata or dropdown option data.
 *  • Keep FormListsLabPage thin and testable.
 *
 * IMPORTANT
 *  • This is NOT save/publish data.
 *  • This does NOT change dropdownOptions.
 *  • This does NOT change fieldsMetadata.
 *  • This does NOT change dependency behavior.
 */

export const buildFormListsLabGroupOrderStorageKey = (formId) => (
  `form_${normalizeString(formId, 'unknown')}_formListsLab_groupOrder`
);

export const buildFormListsLabPanelStateStorageKey = (formId) => (
  `form_${normalizeString(formId, 'unknown')}_formListsLab_panelState`
);

export const getGroupIdsFromGroups = (groups = []) => (
  Array.isArray(groups)
    ? groups.map((group) => getGroupId(group)).filter(Boolean)
    : []
);

export const normalizeGroupOrder = ({
  groups = [],
  groupOrder = [],
} = {}) => {
  const currentGroupIds = getGroupIdsFromGroups(groups);
  const currentGroupIdSet = new Set(currentGroupIds);

  const preservedGroupIds = Array.isArray(groupOrder)
    ? groupOrder.filter((groupId) => currentGroupIdSet.has(groupId))
    : [];

  const preservedSet = new Set(preservedGroupIds);

  const newGroupIds = currentGroupIds.filter(
    (groupId) => !preservedSet.has(groupId)
  );

  return [
    ...preservedGroupIds,
    ...newGroupIds,
  ];
};

export const orderGroupsByGroupOrder = ({
  groups = [],
  groupOrder = [],
} = {}) => {
  const safeGroups = Array.isArray(groups) ? groups : [];
  const normalizedOrder = normalizeGroupOrder({
    groups: safeGroups,
    groupOrder,
  });

  const groupById = safeGroups.reduce((acc, group) => {
    const groupId = getGroupId(group);
    if (!groupId) return acc;

    acc[groupId] = group;
    return acc;
  }, {});

  return normalizedOrder
    .map((groupId) => groupById[groupId])
    .filter(Boolean);
};

export const moveGroupInOrder = ({
  groupOrder = [],
  groupId,
  direction,
} = {}) => {
  const safeGroupId = normalizeString(groupId, '');
  if (!safeGroupId) return groupOrder;

  const nextOrder = Array.isArray(groupOrder)
    ? [...groupOrder]
    : [];

  const currentIndex = nextOrder.indexOf(safeGroupId);
  if (currentIndex < 0) return nextOrder;

  const targetIndex =
    direction === 'up'
      ? currentIndex - 1
      : currentIndex + 1;

  if (targetIndex < 0 || targetIndex >= nextOrder.length) {
    return nextOrder;
  }

  const [item] = nextOrder.splice(currentIndex, 1);
  nextOrder.splice(targetIndex, 0, item);

  return nextOrder;
};

export const reorderGroupOrderByDrop = ({
  groupOrder = [],
  sourceGroupId,
  targetGroupId,
} = {}) => {
  const safeSourceGroupId = normalizeString(sourceGroupId, '');
  const safeTargetGroupId = normalizeString(targetGroupId, '');

  if (
    !safeSourceGroupId ||
    !safeTargetGroupId ||
    safeSourceGroupId === safeTargetGroupId
  ) {
    return groupOrder;
  }

  const nextOrder = Array.isArray(groupOrder)
    ? [...groupOrder]
    : [];

  const sourceIndex = nextOrder.indexOf(safeSourceGroupId);
  const targetIndex = nextOrder.indexOf(safeTargetGroupId);

  if (sourceIndex < 0 || targetIndex < 0) {
    return nextOrder;
  }

  const [item] = nextOrder.splice(sourceIndex, 1);
  nextOrder.splice(targetIndex, 0, item);

  return nextOrder;
};